import Typography from '@alltrails/core/components/Typography';
import { FormattedMessage, useIntl } from 'react-intl';
import Info from '@alltrails/denali/icons/Info';
import { useCallback, useState } from 'react';
import IconButton from '@alltrails/denali/components/IconButton';
import classNames from 'classnames';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import PeakLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Peak';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import { useExperiments } from '@alltrails/experiments';
import { LayerType } from '../../types/LayerMenu';
import styles from './LayerMenu.module.scss';
import HeatmapPrivacyModal from './HeatmapPrivacyModal';

type Props = {
  layerType: LayerType;
  baseMapCards: React.ReactNode;
  conditionsCards?: React.ReactNode;
  extraCards: React.ReactNode;
  heatmapCard?: React.ReactNode;
  layerControl: React.ReactNode;
  onHeatmapPrivacyIconClick: () => void;
};

const LayerCards = ({ layerType, baseMapCards, conditionsCards, extraCards, heatmapCard, layerControl, onHeatmapPrivacyIconClick }: Props) => {
  const intl = useIntl();
  const [isHeatmapPrivacyModalOpen, setIsHeatmapPrivacyModalOpen] = useState(false);
  const isMobile = useIsMobileSizedScreen();
  const { hasPermission } = useAuthorization();
  const isAdmin = hasPermission({ permission: 'trails:manage' });
  const experiments = useExperiments();

  const handleClick = useCallback(() => {
    if (isMobile) {
      onHeatmapPrivacyIconClick();
    } else {
      setIsHeatmapPrivacyModalOpen(true);
    }
  }, [isMobile, onHeatmapPrivacyIconClick]);

  return (
    <>
      <div className={styles.cardsContent}>
        {isMobile && layerControl}
        <>
          {layerType === 'baseMap' && <div className={styles.cardsSection}>{baseMapCards}</div>}
          {layerType === 'overlays' && (
            <div className={styles.overlayCardsSection}>
              {heatmapCard && (
                <div className={styles.overlayCardContent}>
                  <div className={styles.tierSectionHeader}>
                    <div className={styles.heatmapTitle}>
                      <Typography variant="text100bold">
                        <FormattedMessage defaultMessage="Heatmaps" />
                      </Typography>
                      <IconButton
                        size="sm"
                        variant="flat"
                        icon={{ Component: Info }}
                        onClick={handleClick}
                        testId="heatmap-info"
                        title={intl.formatMessage({ defaultMessage: 'Heatmap privacy info' })}
                      />
                    </div>
                    <PeakLogoBadge size="sm" variant="full" />
                  </div>
                  {heatmapCard}
                </div>
              )}
              {conditionsCards ? (
                <div className={styles.overlayCardContent}>
                  <div className={styles.tierSectionHeader}>
                    <Typography variant="text100bold">
                      <FormattedMessage defaultMessage="Conditions" />
                    </Typography>
                    {experiments['rm-conditions-plus']?.value === 'on' ? (
                      <PlusLogoBadge size="sm" variant="full" />
                    ) : (
                      <PeakLogoBadge size="sm" variant="full" />
                    )}
                  </div>
                  <div className={classNames(classNames(styles.cardsSection, styles.conditionsCards), styles.conditionsSection)}>
                    {conditionsCards}
                  </div>
                </div>
              ) : null}
            </div>
          )}
          {layerType === 'extras' && (
            <div className={classNames(styles.cardsSection, { [styles.extraCardsSection]: !isAdmin, [styles.adminExtraCardsSection]: isAdmin })}>
              {extraCards}
            </div>
          )}
        </>
      </div>
      <HeatmapPrivacyModal isOpen={isHeatmapPrivacyModalOpen} onCloseRequest={() => setIsHeatmapPrivacyModalOpen(false)} />
    </>
  );
};

export default LayerCards;
